'use client';

import { useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ScheduledSlotsGrid } from './_components/scheduled-slots-grid.component';
import { ScheduledTabPreview } from './_components/scheduled-tab-preview.component';
import { SearchCardClinicScheduledModal } from './search-card-clinic-scheduled-modal.component';

import { NearScheduleModal } from '@/app/(main)/(pages)/_components/schedules/near-schedule-modal/near-schedule-modal.component';
import { SlotsSkeleton } from '@/app/(main)/(pages)/_components/schedules/slots-skeleton.component';
import { useScheduleSlots } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { useSlotFilters } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-slot-filters.hook';
import { ScheduleType, SchedulesSearchSlot, SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { isScheduleNear } from '@/utils/schedule/is-schedule-near.util';

const CARD_SCHEDULED_DAYS = 3;

interface SearchCardClinicTabScheduledProps {
  schedule: SearchPartnerData;
}

export function SearchCardClinicTabScheduled({ schedule }: SearchCardClinicTabScheduledProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNearScheduleWarningOpen, setIsNearScheduleWarningOpen] = useState(false);
  const [shouldRestoreSlotsModal, setShouldRestoreSlotsModal] = useState(false);
  const scheduledSchedule = schedule.schedulesTypes[ScheduleType.SCHEDULED]?.[0];
  const scheduleId = scheduledSchedule?.id;
  const slotFilters = useSlotFilters();
  const { requestPatientSelection } = usePatientSelection();

  const { isLoading, days, hasMore, offset, goNext, goPrev } = useScheduleSlots({
    scheduleId,
    limitDays: CARD_SCHEDULED_DAYS,
    filters: slotFilters,
  });

  const openModal = () => setIsModalOpen(true);

  const askForPatient = () => {
    requestPatientSelection({
      ageRange: { minAge: scheduledSchedule?.minAge, maxAge: scheduledSchedule?.maxAge },
    });
  };

  const handleSelectSlot = (slot: SchedulesSearchSlot) => {
    setShouldRestoreSlotsModal(isModalOpen);
    setIsModalOpen(false);

    if (isScheduleNear(slot.start)) {
      setIsNearScheduleWarningOpen(true);
      return;
    }

    askForPatient();
  };

  const handleNearScheduleConfirm = () => {
    setIsNearScheduleWarningOpen(false);
    askForPatient();
  };

  const handleNearScheduleDismiss = () => {
    setIsNearScheduleWarningOpen(false);
    setIsModalOpen(shouldRestoreSlotsModal);
  };

  return (
    <div data-testid={DataTestIdEnum.SEARCH_CARD_CLINIC_TAB_SCHEDULED}>
      {isLoading
        ? (
            <>
              <SlotsSkeleton variant="card" className="xl:hidden" />
              <SlotsSkeleton variant="modal" columns={CARD_SCHEDULED_DAYS} className="hidden xl:flex" />
            </>
          )
        : (
            <>
              <ScheduledTabPreview
                day={days[0]}
                className="xl:hidden"
                onSeeMore={openModal}
                onSelectSlot={handleSelectSlot}
              />
              <ScheduledSlotsGrid
                days={days}
                hasMore={hasMore}
                offset={offset}
                className="hidden xl:flex"
                onPrev={goPrev}
                onNext={goNext}
                onSeeMore={openModal}
                onSelectSlot={handleSelectSlot}
              />
            </>
          )}

      <SearchCardClinicScheduledModal
        scheduleId={scheduleId}
        schedule={schedule}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSelectSlot={handleSelectSlot}
      />

      <NearScheduleModal
        open={isNearScheduleWarningOpen}
        partnerId={schedule.partner.id}
        scheduleType={ScheduleType.SCHEDULED}
        onConfirm={handleNearScheduleConfirm}
        onDismiss={handleNearScheduleDismiss}
      />
    </div>
  );
}
